import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { alertActions, preAlertActions } from '_store';
import {
    TextField,
    Typography,
    IconButton,
    FormControl,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { convertOperator, getIcons, history, revertOperator } from '_helpers';
import { CustomAutocomplete } from '_components/CustomAutocomplete';
import { EmailSelectionModal } from '_components/modals';

export { AlertCreateForm };

/**
 * Formulaire de création d'alerte
 * Nécessite un nom et une description obligatoirement
 * Il est possible de rajouter autant de règles que l'on souhaite
 * La partie emails de notification ouvre une 
 * modale de sélection d'emails afin de faciliter la saisie
 * @returns 
 */
function AlertCreateForm () {
    const { preAlertId } = useParams();
    const dispatch = useDispatch();

    const preAlerts = useSelector(state => state.preAlerts?.list);
    const preAlert = useSelector(state => state.preAlerts?.item);
    const variables = useSelector(state => state.preAlerts?.variables);

    const [ show, setShow ] = useState(false);

    const [sections, setSections] = useState({
        generalInfo: true,
        criteria: true,
        notify: true,
    });    

    const title = preAlertId ? 'Modifier la pré-alerte' : 'Créer une pré-alerte';

    /* Gère l'ouverture et la fermeture de la modale */
    const handleShowEmailsModal = (e) => {
        e.preventDefault()
        setShow(true);
    };
    const handleCloseModal = () => {        
        setShow(false);
    };
    
    /* Schema de validation Yup */
    const validationSchema = yup.object().shape({
        generalInfo: yup.object().shape({
            name: yup.string().required('Nom requis'),
            description: yup.string().required('Description requise'),
        }),
    });

    const { control, handleSubmit, setValue, register, watch, reset, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            generalInfo: {name: '', description: ''},
            criteria: [{ champ: '', operateur: '', valeur: '', valeur2: '' }],
            notify: {emails: ''}
        },
    });    

    const criteria = watch('criteria');
    const emailsFromForm = watch('notify.emails') || '';

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'criteria'
    });    

    /* Ajout / Suppression de règles */
    const handleAddRule = () => {
        append({ champ: '', operateur: '', valeur: '', valeur2: '' });
    };
    const handleRemoveRule = (index) => {
        remove(index)
    }

    /* Préparation des données */
    const [fieldData, setFieldData] = useState([]);

    useEffect(() => {
        if(variables){
            const data = Object.keys(variables).map(key => (`${variables[key].description} - (${key})`));
            setFieldData(data)
        }
    }, [variables])

    const operateurData = {
        integer: ['Supérieur ou égal', 'Supérieur', 'Égal', 'Inférieur', 'Inférieure ou égale', 'Compris', 'Nulle'],
        double: ['Supérieur ou égal', 'Supérieur', 'Égal', 'Inférieur', 'Inférieure ou égale', 'Compris', 'Nulle'],
        string: ['Égal', 'Contient', 'Nulle'],
        date: ['Avant', 'Après', 'Entre', 'Nulle']
    };

    /**
     * Extrait le code de puis le champ description pour la communication avec le backend
     * @param {*} value 
     * @returns 
     */
    const getCodeFromChamp = (value) => {
        const parts = value.split(' - ');
        let code = '';
        if (parts.length > 1) {
            const index = parts.length - 1;
            const match = parts[index].match(/\((.*?)\)/);
            if (match) {
                code = match[1]; // Cela contient la partie entre parenthèses
            } 
        }
        return code;
    }
    
    /**
     * Permet de récupérer les bons opérateurs en fonction du type de champ
     * @param {*} champ 
     * @returns 
     */
    const getOperateurData = (champ) => {
        const code = getCodeFromChamp(champ);

        if (!champ || !code || !variables[code]) {
            return [];
        }
        //exception pour le champ WSD
        if (code === "WSD") return operateurData["integer"]
        
        const type = variables[code].type; // Vérifie si le champ est présent dans les données variables
        return operateurData[type] || [];
    };

    /**
     * Enregistre la liste d'emails à la validation de la modale
     * Récupère les emails actuels du champ, sous forme de tableau
     * Concatène les emails existants avec les nouveaux emails sélectionnés
     * Filtre les doublons et les emails vides (Utilise un Set pour éliminer les doublons)
     * Met à jour le champ d'emails avec la nouvelle liste concaténée
     * @param {*} emails 
     */ 
    const handleEmailSelection = (emails) => {
        const { clientEmails, techEmails, otherEmails } = emails;
        const allEmails = [...clientEmails, ...techEmails, ...otherEmails]; 

        const currentEmails = emailsFromForm.map(email => email.trim());

        const combinedEmails = [...currentEmails, ...allEmails]
            .filter((email, index, self) => email && self.indexOf(email) === index);  

        const uniqueEmails = [...new Set(combinedEmails)];
        
        setValue('notify.emails', uniqueEmails.join(', '));    
    };    

    /**
     * Récupération des données au chargement du composant
     */
    useEffect(() => {
        if (!preAlerts) {
            dispatch(preAlertActions.getAll());            
        }
        if(!variables){
            dispatch(preAlertActions.getVariables());
        }
    }, []);

    /**
     * Initialise le formulaire avec les bon paramètres en fonction 
     * de la page actuelle: création / modification
     */
    useEffect(() => {
        if (preAlertId && preAlerts && variables) {
            if (!preAlert) {
                const preAlert = preAlerts[preAlertId];
                dispatch(preAlertActions.setPreAlert(preAlert))
                initializeForm(preAlert, variables);
            } else {
                initializeForm(preAlert, variables);
            }
        }
    }, [preAlertId, preAlerts, preAlert, variables, dispatch]);

    /**
     * Initialisation du formulaire
     * @param {*} preAlert 
     * @param {*} variables 
     */
    const initializeForm = (preAlert, variables) => {
        if (preAlert && variables) {
            const filterToPopulate = {}
            setValue('generalInfo.name', preAlert.name);
            setValue('generalInfo.description', preAlert.description);
            setValue('notify.emails', preAlert.emails.join(',\n'));

            const criteria = preAlert.rules.map(rule => ({
                champ:  `${variables[rule.champ].description} - (${rule.champ})` ,
                operateur: rule.operateur === 'compris entre'
                    ? 'Compris'
                    : rule.operateur === 'Nulle'
                        ? 'Nulle'
                        : revertOperator(rule.operateur),
                valeur: rule.operateur === 'Nulle'
                    ? ''   // pas de champ à remplir
                    : (rule.valeur instanceof Array ? rule.valeur[0] : rule.valeur || ''),
                valeur2: rule.valeur instanceof Array ? rule.valeur[1] : ''
            }));

            setValue('criteria', criteria);
            filterToPopulate.generalInfo = {name: preAlert.name, description:  preAlert.description}
            filterToPopulate.criteria = criteria;
            reset(filterToPopulate);
        }
    };

    /**
     * Soumission du formulaire
     * Chaque règle doit etre envoyée sous la forme d'un objet:
     * rule: {champ: xxx, valeur: xxx, operateur: xxx} ou {champ: xxx, valeur: xxx, valeur2: xxx, operateur: xxx}
     * Dans le cas d'une valeur 'comprise entre' il y a une deuxième valeur et valeur devient un tableau
     * L'opérateur est au format numérique (>,<, = ...) sauf pour compris entre, contient et null
     * L'envoie de la requete varie si l'on est dans une création ou une modification
     * @param {*} data 
     */
    async function onSubmit(data) {
        dispatch(alertActions.clear());
        let message;
        try {
            const { generalInfo, criteria, notify } = data;
            
            const rules = [];

            const addRule = (key, rule) => {
                const code = getCodeFromChamp(rule.champ)
                if (rule.operateur === 'Compris') {
                    rules.push({ champ: code, valeur: [rule.valeur, rule.valeur2], operateur: 'compris entre' });
                } else if (rule.operateur === 'Contient') {
                    rules.push({ champ: code, valeur: [rule.valeur, rule.valeur2], operateur: 'contient' });
                } else if (rule.operateur === 'Nulle') {
                    rules.push({ champ: code, operateur: 'Nulle' });
                } else if (rule.valeur !== '' && rule.valeur !== null) {
                    const sign = convertOperator(rule.operateur)
                    rules.push({ champ: code, valeur: rule.valeur, operateur: sign });
                }
            };

            Object.keys(criteria).forEach((key) => {
                addRule(key, criteria[key]);
            });

            if (preAlertId){    
                const preAlert = {
                    id: preAlertId,
                    name: generalInfo.name,
                    description: generalInfo.description,
                    emails: notify.emails,
                    rules: rules,
                }

                const response = await dispatch(preAlertActions.update(preAlert));
                
                if (response.meta.requestStatus === 'rejected') {
                    const errorMessage = response.error?.message || 'La modification de la pré alerte a échoué.';
                    throw new Error(errorMessage);
                }  else {
                    message = 'Pré-alerte mise à jour avec succès';
                }
            } else {
                const preAlert = {
                    name: generalInfo.name,
                    description: generalInfo.description,
                    emails: notify.emails,
                    rules: rules,
                }

                const response = await dispatch(preAlertActions.create(preAlert)).unwrap();

                if (response?.meta?.requestStatus === 'rejected') {
                    const errorMessage = response.error?.message || 'La création de la pré alerte a échoué.';
                    throw new Error(errorMessage);
                }  else {
                    message = 'Pré-alerte créée avec succès';
                }
            }

            // Redirection vers la page adéquate après validation
            let url = "/alertes/gestion";
            const { from } = history.location.state || { from: { pathname: url } };
            history.navigate(from);

            //Notification de l'utilisateur
            dispatch(alertActions.success({ message, showAfterRedirect: true }));
            dispatch(preAlertActions.getAll());
        } catch (error) {
            dispatch(alertActions.error(error));
        }        
    };

    /**
     * Permet d'afficher / masquer une section
     * @param {*} section 
     */
    const toggleSection = (section) => {
        setSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const InputProps={
        className: 'leading-none'
    }

    return (<>
        {variables && 
        <div>
            <div className="px-10 pb-4 title-customtable">
                {title}
            </div>
            <div className='px-5'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Info générales */}
                    <div className="mb-2">
                        <div className="flex items-center justify-between">
                            <Typography variant="h6">Informations générales</Typography>
                            <IconButton onClick={() => toggleSection('generalInfo')}>
                                {sections.generalInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.generalInfo && (
                        <div>
                            <TextField
                                {...register('generalInfo.name')}
                                label="Nom de la pré-alerte"
                                error={!!errors.generalInfo?.name}
                                helperText={errors.generalInfo?.name?.message}
                                className='form-control w-1/2 mb-2'
                                InputProps={InputProps}
                                size="small"
                            />
                            <TextField
                                {...register('generalInfo.description')}
                                width="50%"
                                label="Description"
                                multiline
                                rows={4}
                                error={!!errors.generalInfo?.description}
                                helperText={errors.generalInfo?.description?.message}
                                className='form-control'
                                size="small"
                            />                            
                        </div>
                        )}
                    </div>      
                    {/* Critères */}
                    <div className="mb-2 mt-4">
                        <div className="flex justify-between align-center">
                            <Typography variant="h6">Critères</Typography>
                            <IconButton onClick={() => toggleSection('criteria')}>
                                {sections.criteria ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.criteria && (
                            <div>  
                                {fields.map((item, index) => {
                                    const operateur = watch(`criteria[${index}].operateur`);
                                    return (
                                    <div key={item.id} className="flex align-center mb-2">
                                        <FormControl sx={{ minWidth: 200, marginRight: 2 }} className='w-1/4'>
                                            <Controller
                                                control={control}
                                                name={`criteria[${index}].champ`}
                                                render={({ field }) =>                                             
                                                    <CustomAutocomplete
                                                        {...field}
                                                        label="Quand"
                                                        value={field.value || null} 
                                                        options={fieldData}
                                                        onInputChange={(value) => {                                                            
                                                            setValue(`criteria[${index}].operateur`, ''); 
                                                            field.onChange(value)
                                                        }}
                                                        onChange={() => false}
                                                        onClear={() => {
                                                            field.onChange('')
                                                            setValue(`criteria[${index}].operateur`, ''); 
                                                            setValue(`criteria[${index}].valeur`, ''); 
                                                            setValue(`criteria[${index}].valeur2`, '')
                                                        }}                                                        
                                                    />
                                                }                                            
                                            />
                                        </FormControl>
                                        <FormControl sx={{ minWidth: 200, marginRight: 2 }} className='w-1/4'>
                                            <Controller
                                                control={control}
                                                name={`criteria[${index}].operateur`}
                                                render={({ field }) => {
                                                    const operateurOptions = getOperateurData(watch(`criteria[${index}].champ`));
                                                    return (
                                                    <CustomAutocomplete
                                                        {...field}
                                                        label="a une valeur"
                                                        value={field.value || null} 
                                                        options={operateurOptions}
                                                        onInputChange={(value) => {
                                                            if (value === ''){
                                                                setValue(`criteria[${index}].valeur`, ''); // Reset valeur
                                                                setValue(`criteria[${index}].valeur2`, ''); // Reset valeur2 if applicable
                                                            }
                                                            field.onChange(value)
                                                        }}
                                                        onChange={() => false}
                                                        onClear={() => {
                                                            setValue(`criteria[${index}].valeur`, ''); // Reset valeur
                                                            setValue(`criteria[${index}].valeur2`, ''); // Reset valeur2 if applicable
                                                            field.onChange('')}
                                                        }                                                        
                                                    />);
                                                }}
                                            />
                                        </FormControl>
                                        {operateur === 'Compris' ? (
                                        <>
                                            <TextField
                                                type={variables[getCodeFromChamp(criteria[index]?.champ)]?.type === 'integer' ? "number" : "text"}
                                                label="entre"
                                                {...register(`criteria[${index}].valeur`)}
                                                sx={{ marginRight: 2 }}
                                                className='form-control w-1/6'
                                            />
                                            <TextField
                                                type={variables[getCodeFromChamp(criteria[index]?.champ)]?.type === 'integer' ? "number" : "text"}
                                                label="et"
                                                {...register(`criteria[${index}].valeur2`)}
                                                sx={{ marginRight: 2 }}
                                                className='form-control w-1/6'
                                            />
                                        </>
                                        ) : (operateur !== 'Nulle' ? (
                                        <TextField
                                            type={variables[getCodeFromChamp(criteria[index]?.champ)]?.type === 'integer' ? "number" : "text"}
                                            label="à"
                                            {...register(`criteria[${index}].valeur`)}
                                            sx={{ marginRight: 2 }}
                                            className='form-control w-1/6'
                                        />     
                                        ) : <></>)}                                      
                                        {index !== 0 && 
                                        <button 
                                            type="button"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                handleRemoveRule(index);
                                            }}  
                                        >
                                            <img src={getIcons('clearOutlinedGrey')} alt='supprimer la ligne' />                                        
                                        </button> }                                       
                                    </div>
                                    );
                                })}
                                <div className='my-2'>
                                    <button variant="contained" 
                                            onClick={(event) => {
                                                event.preventDefault();
                                                handleAddRule();
                                            }}  
                                            className='btn-custom btn-custom-primary'>
                                        <img src={getIcons('addOutlined')} className='btn-custom-icon mt-[-4px]' alt='manage alerts' />
                                        Ajouter une règle
                                    </button>
                                </div>   
                            </div>                            
                        )}
                    </div>

                    {/* Notification */}
                    <div className="my-4" >
                        <div className="flex justify-between align-center " >
                            {<Typography variant="h6">Notification mail</Typography>}
                            <IconButton onClick={() => toggleSection('notify')}>
                                {sections.notify ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.notify && (                            
                            <div className="flex space-between w-full">                                    
                                <div sx={{ display: 'flex', flexDirection: 'column' }} className="basis-2/3">
                                    <Typography variant="subtitle1" className='my-2'>
                                        Envoie un email aux destinataires dès que cette alerte est remontée par un bus<br/>
                                        <small>Vous pouvez ajouter manuellement des adresses email (séparées par des virgules) ou avec le bouton 'AJOUTER DES DESTINATAIRES'</small>
                                    </Typography>
                                    <TextField                                    
                                        {...register('notify.emails', {
                                            setValueAs: value => value.split(',').map(email => email.trim())  // Transformer la chaîne en tableau
                                        })}  // Transforme le tableau d'emails en une chaîne de caractères séparée par des virgules
                                        label="Emails sélectionnés"
                                        className='form-control my-2'                                        
                                        multiline
                                        size='large'
                                    />                                      
                                    <button onClick={handleShowEmailsModal} variant="contained" className="btn-custom btn-custom-primary mt-4 mb-6">
                                        <img src={getIcons("emailOutlinedWhite")} alt="icon return" className="btn-custom-icon"/>
                                        Ajouter des destinataires
                                </button>                                 
                                </div>
                                                                       
                            </div>   
                        )}
                    </div>
                    {/* Modale */}
                    <EmailSelectionModal
                        show={show}
                        handleClose={handleCloseModal}                
                        onEmailSelection={handleEmailSelection}
                    />
                    {/* Soumission */}
                    <button type="submit" variant="contained" className="btn-custom btn-custom-primary mt-4 mb-6 float-right">
                        <img src={getIcons("checkOutlined")} alt="icon return" className="btn-custom-icon"/>
                        Enregistrer
                    </button>
                </form>
            </div>
        </div>
        }
        </>
    );
};


